import React, { useMemo, useState } from 'react';
import { Table2, LineChart as LineIcon, Loader2 } from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, ComposedChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';
import { Button, ErrorBlock, Segmented, Skeleton, money, useLoad } from '../kit';
import { API, CoordinatorPicker, FigureLine, monthLabel, monthLong, shortDate } from './shared';

// Trends: four plain charts, each answering one question, with its numbers a click away (table view).
// Colours (checked with the dataviz palette validator for colour-blind separation): violet for
// Support Coordination's own measures, emerald / sky / rose only where they mean paid / waiting /
// rejected, emerald for accepted referrals.
const COL = {
  billable: '#6d28d9', nonbillable: '#a78bfa',
  caseload: '#a78bfa', withTime: '#5b21b6',
  paid: '#059669', waiting: '#0ea5e9', rejected: '#e11d48',
  received: '#8b5cf6', accepted: '#047857',
};
const AXIS = { fontSize: 12, fill: '#64748b' };
const AXIS_TITLE = { fontSize: 12, fill: '#475569', fontWeight: 600 };
const GRID = '#eef2f7';
const RANGE = [{ value: 3, label: '3 months' }, { value: 6, label: '6 months' }, { value: 12, label: '12 months' }];
const h2 = (n) => `${Number(n || 0).toFixed(2)} h`;
const compactMoney = (n) => (Math.abs(n) >= 1000 ? `$${(n / 1000).toLocaleString('en-AU', { maximumFractionDigits: 1 })}k` : `$${Math.round(n)}`);
const legendText = (v) => <span style={{ color: '#475569', fontSize: 12 }}>{v}</span>;

export default function TrendsTab({ months, onMonths, coordinator, onCoordinator }) {
  const params = useMemo(() => ({ months, ...(coordinator ? { coordinator_id: coordinator } : {}) }), [months, coordinator]);
  const { data, loading, error, reload } = useLoad(`${API}/trends`, params);

  const hours = useMemo(() => (data && data.hours ? data.hours.map((w) => ({ label: shortDate(w.week_start), long: `Week of ${shortDate(w.week_start)}`, Billable: w.billable_hours, 'Non-billable': w.nonbillable_hours })) : null), [data]);
  const people = useMemo(() => (data ? (data.participants || []).map((m) => ({ label: monthLabel(m.month), long: monthLong(m.month), 'On the caseload': m.on_caseload, 'With time logged': m.with_time })) : []), [data]);
  const claims = useMemo(() => (data && data.claims ? data.claims.map((m) => ({ label: monthLabel(m.month), long: monthLong(m.month), Paid: m.paid, Waiting: m.waiting, Rejected: m.rejected, notPaid: m.not_paid })) : null), [data]);
  const refs = useMemo(() => (data ? (data.referrals || []).map((m) => ({ label: monthLabel(m.month), long: monthLong(m.month), Received: m.received, Accepted: m.accepted, sent: m.sent })) : []), [data]);

  const sum = (rows, k) => (rows || []).reduce((t, r) => t + (Number(r[k]) || 0), 0);
  const hoursTotal = sum(hours, 'Billable') + sum(hours, 'Non-billable');
  const claimsTotal = sum(claims, 'Paid') + sum(claims, 'Waiting') + sum(claims, 'Rejected') + sum(claims, 'notPaid');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          <div role="group" aria-labelledby="trend-range-l" className="space-y-1.5">
            <span id="trend-range-l" className="block text-[12.5px] font-semibold text-slate-700">Range</span>
            <Segmented value={months} onChange={onMonths} options={RANGE} />
          </div>
          <CoordinatorPicker value={coordinator} onChange={onCoordinator} coordinators={data && data.coordinators} />
        </div>
        {loading && data && <span className="inline-flex items-center gap-1.5 text-[12px] text-slate-500"><Loader2 size={13} className="animate-spin" aria-hidden /> Updating…</span>}
      </div>

      {error && !data ? <ErrorBlock error={error} onRetry={reload} />
        : !data ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" aria-busy="true" aria-label="Loading">
            {[0, 1, 2, 3].map((i) => <div key={i} className="bg-white rounded-2xl border border-slate-200/80 p-5 space-y-3"><Skeleton className="h-5 w-48" /><Skeleton className="h-56" /></div>)}
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {hours && (
              <ChartPanel id="tr-hours" title="Hours logged per week" sub="Billable and non-billable time entries, by the week the work was done."
                empty={!hoursTotal} emptyText="No time logged in this range."
                table={<DataTable rows={hours} cols={[['long', 'Week'], ['Billable', 'Billable', h2], ['Non-billable', 'Non-billable', h2]]} />}
                footer={<FigureLine items={[{ label: 'Total', value: h2(hoursTotal) }, { label: 'Billable', value: hoursTotal ? `${Math.round((sum(hours, 'Billable') / hoursTotal) * 100)}%` : '—' }]} />}>
                <BarChart data={hours} margin={{ top: 8, right: 8, left: 4, bottom: 18 }} barCategoryGap="18%">
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: '#cbd5e1' }} minTickGap={16}
                    label={{ value: 'Week starting', position: 'insideBottom', offset: -12, style: AXIS_TITLE }} />
                  <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} allowDecimals={false}
                    label={{ value: 'Hours', angle: -90, position: 'insideLeft', offset: 8, style: AXIS_TITLE }} />
                  <Tooltip content={<Tip fmt={h2} />} cursor={{ fill: 'rgba(124,58,237,0.06)' }} />
                  <Legend verticalAlign="top" align="right" iconType="square" iconSize={10} height={28} formatter={legendText} />
                  <Bar dataKey="Billable" stackId="h" fill={COL.billable} stroke="#fff" strokeWidth={1} maxBarSize={28} />
                  <Bar dataKey="Non-billable" stackId="h" fill={COL.nonbillable} stroke="#fff" strokeWidth={1} maxBarSize={28} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartPanel>
            )}

            <ChartPanel id="tr-people" title="Participants" sub="On the caseload at the end of each month (today for this month), and how many had time logged that month."
              empty={!sum(people, 'On the caseload') && !sum(people, 'With time logged')} emptyText="No participants in this range."
              table={<DataTable rows={people} cols={[['long', 'Month'], ['On the caseload', 'On the caseload'], ['With time logged', 'With time logged']]} />}>
              <ComposedChart data={people} margin={{ top: 8, right: 8, left: 4, bottom: 18 }}>
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: '#cbd5e1' }}
                  label={{ value: 'Month', position: 'insideBottom', offset: -12, style: AXIS_TITLE }} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} allowDecimals={false}
                  label={{ value: 'Participants', angle: -90, position: 'insideLeft', offset: 8, style: AXIS_TITLE }} />
                <Tooltip content={<Tip />} cursor={{ fill: 'rgba(124,58,237,0.06)' }} />
                <Legend verticalAlign="top" align="right" iconSize={10} height={28} formatter={legendText} />
                <Bar dataKey="On the caseload" fill={COL.caseload} maxBarSize={32} radius={[4, 4, 0, 0]} />
                <Line dataKey="With time logged" stroke={COL.withTime} strokeWidth={2} dot={{ r: 5, fill: COL.withTime, stroke: '#fff', strokeWidth: 1.5 }} activeDot={{ r: 6 }} />
              </ComposedChart>
            </ChartPanel>

            {claims && (
              <ChartPanel id="tr-claims" title="Claims by month of service" sub="Claim lines by the month the support was delivered: paid, still waiting for a result, and rejected."
                empty={!claimsTotal} emptyText="No claim lines with a service date in this range."
                table={<DataTable rows={claims} cols={[['long', 'Month'], ['Paid', 'Paid', money], ['Waiting', 'Waiting', money], ['Rejected', 'Rejected', money], ['notPaid', 'Written off or capped', money]]} />}
                footer={<FigureLine items={[
                  { label: 'Paid', value: money(sum(claims, 'Paid')) },
                  { label: 'Waiting', value: money(sum(claims, 'Waiting')) },
                  { label: 'Rejected', value: money(sum(claims, 'Rejected')), tone: sum(claims, 'Rejected') ? 'red' : undefined },
                  sum(claims, 'notPaid') > 0 && { label: 'Written off or capped', value: money(sum(claims, 'notPaid')) },
                ]} />}>
                <BarChart data={claims} margin={{ top: 8, right: 8, left: 4, bottom: 18 }} barCategoryGap="22%">
                  <CartesianGrid stroke={GRID} vertical={false} />
                  <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: '#cbd5e1' }}
                    label={{ value: 'Month of service', position: 'insideBottom', offset: -12, style: AXIS_TITLE }} />
                  <YAxis tick={AXIS} tickLine={false} axisLine={false} width={52} tickFormatter={compactMoney}
                    label={{ value: 'Amount', angle: -90, position: 'insideLeft', offset: 4, style: AXIS_TITLE }} />
                  <Tooltip content={<Tip fmt={money} />} cursor={{ fill: 'rgba(124,58,237,0.06)' }} />
                  <Legend verticalAlign="top" align="right" iconType="square" iconSize={10} height={28} formatter={legendText} />
                  <Bar dataKey="Paid" stackId="c" fill={COL.paid} stroke="#fff" strokeWidth={1} maxBarSize={32} />
                  <Bar dataKey="Waiting" stackId="c" fill={COL.waiting} stroke="#fff" strokeWidth={1} maxBarSize={32} />
                  <Bar dataKey="Rejected" stackId="c" fill={COL.rejected} stroke="#fff" strokeWidth={1} maxBarSize={32} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ChartPanel>
            )}

            <ChartPanel id="tr-refs" title="Referrals received" sub="Referrals received each month, and how many of those were accepted."
              empty={!sum(refs, 'Received') && !sum(refs, 'sent')} emptyText="No referrals in this range."
              table={<DataTable rows={refs} cols={[['long', 'Month'], ['Received', 'Received'], ['Accepted', 'Accepted'], ['sent', 'Sent to providers']]} />}
              footer={<FigureLine items={[
                { label: 'Received', value: sum(refs, 'Received') },
                { label: 'Accepted', value: sum(refs, 'Received') ? `${sum(refs, 'Accepted')} (${Math.round((sum(refs, 'Accepted') / sum(refs, 'Received')) * 100)}%)` : sum(refs, 'Accepted') },
                { label: 'Sent to providers', value: sum(refs, 'sent') },
              ]} />}>
              <BarChart data={refs} margin={{ top: 8, right: 8, left: 4, bottom: 18 }} barGap={2} barCategoryGap="22%">
                <CartesianGrid stroke={GRID} vertical={false} />
                <XAxis dataKey="label" tick={AXIS} tickLine={false} axisLine={{ stroke: '#cbd5e1' }}
                  label={{ value: 'Month received', position: 'insideBottom', offset: -12, style: AXIS_TITLE }} />
                <YAxis tick={AXIS} tickLine={false} axisLine={false} width={44} allowDecimals={false}
                  label={{ value: 'Referrals', angle: -90, position: 'insideLeft', offset: 8, style: AXIS_TITLE }} />
                <Tooltip content={<Tip />} cursor={{ fill: 'rgba(124,58,237,0.06)' }} />
                <Legend verticalAlign="top" align="right" iconType="square" iconSize={10} height={28} formatter={legendText} />
                <Bar dataKey="Received" fill={COL.received} maxBarSize={22} radius={[4, 4, 0, 0]} />
                <Bar dataKey="Accepted" fill={COL.accepted} maxBarSize={22} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ChartPanel>
          </div>
        )}
    </div>
  );
}

// A chart with its numbers one click away. The chart is described for screen readers; the table is
// the accessible view.
function ChartPanel({ id, title, sub, children, table, footer, empty, emptyText }) {
  const [asTable, setAsTable] = useState(false);
  return (
    <section aria-labelledby={id} className="bg-white rounded-2xl border border-slate-200/80 flex flex-col min-w-0">
      <div className="flex items-start justify-between gap-3 px-4 sm:px-5 pt-4 pb-2">
        <div className="min-w-0">
          <h2 id={id} className="text-[15px] font-semibold text-slate-900 leading-6">{title}</h2>
          <p className="text-[12.5px] text-slate-500 mt-0.5">{sub}</p>
        </div>
        {!empty && (
          <Button variant="ghost" size="sm" icon={asTable ? LineIcon : Table2} onClick={() => setAsTable((v) => !v)} aria-pressed={asTable}>
            {asTable ? 'Show chart' : 'Show table'}
          </Button>
        )}
      </div>
      <div className="px-2 sm:px-3 pb-3 flex-1">
        {empty ? <p className="px-2 py-16 text-center text-[13px] text-slate-600">{emptyText}</p>
          : asTable ? <div className="px-2">{table}</div>
            : (
              <div className="h-64" role="img" aria-label={`${title}. Choose Show table for the numbers.`}>
                <ResponsiveContainer width="100%" height="100%">{children}</ResponsiveContainer>
              </div>
            )}
      </div>
      {footer && !empty && <div className="px-4 sm:px-5 py-3 border-t border-slate-100">{footer}</div>}
    </section>
  );
}

function DataTable({ rows, cols }) {
  return (
    <div className="overflow-x-auto max-h-64 overflow-y-auto">
      <table className="w-full text-[13px]">
        <thead className="sticky top-0 bg-white">
          <tr className="text-[12px] text-slate-600 border-b border-slate-200">
            {cols.map(([k, label], i) => <th key={k} scope="col" className={`font-semibold py-2 px-2 ${i ? 'text-right' : 'text-left'}`}>{label}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.long}>
              {cols.map(([k, , fmt], i) => (
                i ? <td key={k} className="py-1.5 px-2 text-right tabular-nums text-slate-700">{fmt ? fmt(r[k]) : r[k]}</td>
                  : <th key={k} scope="row" className="py-1.5 px-2 text-left font-medium text-slate-800 whitespace-nowrap">{r[k]}</th>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Tip({ active, payload, fmt }) {
  if (!active || !payload || !payload.length) return null;
  const row = payload[0].payload || {};
  return (
    <div className="bg-white rounded-xl ring-1 ring-slate-200 shadow-lg px-3 py-2 text-[12.5px]">
      <p className="font-semibold text-slate-800 mb-1">{row.long}</p>
      {payload.map((p) => (
        <p key={p.dataKey} className="flex items-center gap-2">
          <span className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ background: p.color }} aria-hidden />
          <span className="text-slate-600">{p.name}</span>
          <span className="ml-auto pl-3 font-semibold text-slate-900 tabular-nums">{fmt ? fmt(p.value) : p.value}</span>
        </p>
      ))}
    </div>
  );
}
